<template>
  <div class="row mb-4 gy-3">
    <div class="col-md-3">
      <label for="premiumCategoryFilter" class="form-label">Category</label>
      <select
        id="premiumCategoryFilter"
        :value="filters.category"
        @input="
          updateFilter('category', ($event.target as HTMLSelectElement).value)
        "
        class="form-select"
      >
        <option value="">All Categories</option>
        <option
          v-for="category in availableCategories"
          :key="category"
          :value="category"
        >
          {{ category.replace("_", " ").toUpperCase() }}
        </option>
      </select>
    </div>
    <div class="col-md-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label for="premiumTypeFilter" class="form-label mb-0"
          >Premium Types</label
        >
        <div>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary me-1"
            @click="selectAllPremiumTypes"
          >
            Select All
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="clearAllPremiumTypes"
          >
            Clear All
          </button>
        </div>
      </div>
      <select
        id="premiumTypeFilter"
        :value="filters.premium_types"
        @change="onPremiumTypeSelectionChange"
        class="form-select vintage-multiselect"
        multiple
        size="4"
      >
        <option
          v-for="type in availableTypesForCategory"
          :key="type"
          :value="type"
          :selected="filters.premium_types.includes(type)"
        >
          {{ type }}
        </option>
      </select>
      <small class="text-muted">
        Hold Ctrl/Cmd to select multiple. Leave empty for all types.
        <br />
        <strong>Selected:</strong>
        {{
          filters.premium_types.length > 0
            ? filters.premium_types.join(", ")
            : "All premium types"
        }}
      </small>
    </div>
    <div class="col-md-3">
      <label for="premiumDateFromFilter" class="form-label">Date From</label>
      <input
        id="premiumDateFromFilter"
        :value="filters.date_from"
        @input="
          updateFilter('date_from', ($event.target as HTMLInputElement).value)
        "
        type="date"
        class="form-control"
      />
    </div>
    <div class="col-md-3">
      <label for="premiumDateToFilter" class="form-label">Date To</label>
      <input
        id="premiumDateToFilter"
        :value="filters.date_to"
        @input="
          updateFilter('date_to', ($event.target as HTMLInputElement).value)
        "
        type="date"
        class="form-control"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { EacPremium } from "@/market_insights/admin/eacPricingTypes";

interface PremiumFilters {
  category: string;
  premium_types: string[];
  date_from: string;
  date_to: string;
}

const props = defineProps<{
  premiumHistory: EacPremium[];
}>();

const filters = defineModel<PremiumFilters>("filters", { required: true });

// Computed properties
const availableCategories = computed(() => {
  const categories = [
    ...new Set(props.premiumHistory.map((premium) => premium.category)),
  ].sort();
  return categories;
});

const availableTypes = computed(() => {
  const types = [
    ...new Set(props.premiumHistory.map((premium) => premium.premium_type)),
  ].sort();
  return types;
});

const availableTypesForCategory = computed(() => {
  if (!filters.value.category) {
    return availableTypes.value;
  }

  const types = [
    ...new Set(
      props.premiumHistory
        .filter((premium) => premium.category === filters.value.category)
        .map((premium) => premium.premium_type)
    ),
  ].sort();
  return types;
});

const updateFilter = (key: keyof PremiumFilters, value: string | string[]) => {
  const updatedFilters = { ...filters.value, [key]: value };

  // Clear premium types when category changes
  if (key === "category") {
    updatedFilters.premium_types = [];
  }

  filters.value = updatedFilters;
};

const onPremiumTypeSelectionChange = (event: Event) => {
  const select = event.target as HTMLSelectElement;
  const selectedValues = Array.from(select.selectedOptions).map(
    (option) => option.value
  );
  updateFilter("premium_types", selectedValues);
};

const selectAllPremiumTypes = () => {
  updateFilter("premium_types", [...availableTypesForCategory.value]);
};

const clearAllPremiumTypes = () => {
  updateFilter("premium_types", []);
};
</script>

<style lang="scss" scoped>
/* Vintage multiselect styling */
.vintage-multiselect {
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem;
}

.vintage-multiselect option {
  padding: 8px 12px;
  margin: 2px 0;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.vintage-multiselect option:checked,
.vintage-multiselect option:selected {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
  color: white !important;
  font-weight: 600;
}

.vintage-multiselect option:hover {
  background: #f3f4f6;
}

.vintage-multiselect option:checked:hover,
.vintage-multiselect option:selected:hover {
  background: linear-gradient(135deg, #5855eb 0%, #7c3aed 100%) !important;
  color: white !important;
}
</style>
